import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Icon from '../../../components/AppIcon';
import { foodService } from '../../../services/foodService';
import { stripeService } from '../../../services/stripeService';
import { whatsappService } from '../../../services/whatsappService';
import { supabase } from '../../../lib/supabase';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ clientSecret, orderData, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const { error: stripeError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/#/food/checkout-success`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        setErrorMessage(stripeError?.message || 'Pagamento fallito');
      } else if (paymentIntent && paymentIntent?.status === 'succeeded') {
        await foodService?.updateOrderPaymentStatus(orderData?.orderId, 'paid', paymentIntent?.id);
        await whatsappService?.sendOrderNotification(orderData?.orderId);
        setShowSuccess(true);
        
        setTimeout(() => {
          foodService?.clearCart();
          onSuccess(orderData?.orderId);
        }, 3000);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setErrorMessage('Errore durante il pagamento. Riprova.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="text-center py-12">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
          <Icon name="Check" size={48} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-green-600 mb-2">Pagamento confermato!</h2>
        <p className="text-muted-foreground mb-4">Il tuo ordine è stato ricevuto</p>
        <div className="flex justify-center space-x-1">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Dettagli pagamento</h3>
        <PaymentElement />
      </div>
      {errorMessage && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <p className="text-destructive text-sm">{errorMessage}</p>
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full bg-primary text-primary-foreground rounded-xl px-6 py-4 hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
      >
        {isProcessing ? (
          <>
            <Icon name="Loader2" size={20} className="animate-spin" />
            <span>Elaborazione...</span>
          </>
        ) : (
          <>
            <Icon name="CreditCard" size={20} />
            <span className="font-semibold">Paga €{orderData?.orderTotal?.toFixed(2)}</span>
          </>
        )}
      </button>
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Icon name="Shield" size={14} />
        <span>Pagamento sicuro con Stripe</span>
      </div>
    </form>
  );
};

const FoodCheckout = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [vendorData, setVendorData] = useState(null);
  const [cityData, setCityData] = useState(null);
  const [deliverySlots, setDeliverySlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState('');
  const [orderId, setOrderId] = useState(null);

  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    deliveryAddress: '',
    deliverySlot: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const totals = useMemo(() => {
    return foodService?.calculateCartTotals(cart);
  }, [cart]);

  useEffect(() => {
    loadCheckoutData();
  }, []);

  const loadCheckoutData = async () => {
    try {
      setLoading(true);
      
      const storedCart = foodService?.getCart();
      
      if (!storedCart?.items || storedCart?.items?.length === 0) {
        navigate('/food/cart');
        return;
      }

      setCart(storedCart?.items || []);

      const vendor = await foodService?.getVendorById(storedCart?.vendorId);
      setVendorData(vendor);

      let city = null;
      if (vendor?.city_id) {
        const { data, error } = await supabase?.from('cities_food')?.select('*')?.eq('id', vendor?.city_id)?.single();
        if (error) {
          console.error('Error loading city:', error);
        } else {
          city = data;
        }
      }
      
      if (city) {
        setCityData(city);
        
        try {
          const { data: slotsData, error: slotsError } = await supabase
            ?.from('delivery_slots')
            ?.select('*')
            ?.eq('city_id', city?.id)
            ?.eq('active', true)
            ?.order('start_time');
          
          if (slotsError) {
            console.error('Error loading delivery slots:', slotsError);
            setDeliverySlots([]);
          } else {
            setDeliverySlots(slotsData || []);
          }
        } catch (err) {
          console.error('Exception loading delivery slots:', err);
          setDeliverySlots([]);
        }
      }
    } catch (err) {
      console.error('Error loading checkout data:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData?.customerName?.trim()) {
      errors.customerName = 'Nome obbligatorio';
    }
    if (!formData?.customerPhone?.trim()) {
      errors.customerPhone = 'Telefono obbligatorio';
    }
    if (!formData?.deliveryAddress?.trim()) {
      errors.deliveryAddress = 'Indirizzo obbligatorio';
    }
    if (!formData?.deliverySlot) {
      errors.deliverySlot = 'Seleziona una fascia oraria';
    }

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleCreateOrder = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const order = await foodService?.createOrder({
        cityId: cityData?.id,
        vendorId: vendorData?.id,
        customerName: formData?.customerName,
        customerPhone: formData?.customerPhone,
        deliveryAddress: formData?.deliveryAddress,
        deliverySlot: formData?.deliverySlot,
        productsTotal: totals?.productsTotal,
        deliveryFee: totals?.deliveryFee,
        orderTotal: totals?.orderTotal,
        notes: formData?.notes,
        paymentStatus: 'pending'
      });

      setOrderId(order?.id);

      await foodService?.createOrderItems(order?.id, cart);

      const paymentData = await stripeService?.createPaymentIntent({
        orderId: order?.id,
        amount: totals?.orderTotal,
        currency: 'EUR',
        customerName: formData?.customerName,
        customerPhone: formData?.customerPhone,
        items: cart
      });

      setClientSecret(paymentData?.clientSecret);
    } catch (err) {
      console.error('Error creating order:', err);
      alert('Errore nella creazione dell\'ordine. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (orderId) => {
    navigate(`/food/order-confirmation?orderId=${orderId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-12">
      <div className="bg-card shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <button
            onClick={() => navigate('/food/cart')}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Torna al carrello</span>
          </button>
          <h1 className="text-2xl font-bold text-foreground">Checkout</h1>
          {vendorData && (
            <p className="text-muted-foreground">Ordine da {vendorData?.name}</p>
          )}
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            {!clientSecret ? (
              <>
                <div className="bg-card border border-border rounded-xl p-6">
                  <h2 className="text-xl font-bold text-foreground mb-4">Dati cliente</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Nome e Cognome *</label>
                      <input
                        type="text"
                        value={formData?.customerName}
                        onChange={(e) => setFormData({ ...formData, customerName: e?.target?.value })}
                        className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="Mario Rossi"
                      />
                      {formErrors?.customerName && (
                        <p className="text-destructive text-sm mt-1">{formErrors?.customerName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Telefono *</label>
                      <input
                        type="tel"
                        value={formData?.customerPhone}
                        onChange={(e) => setFormData({ ...formData, customerPhone: e?.target?.value })}
                        className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="+39 333 1234567"
                      />
                      {formErrors?.customerPhone && (
                        <p className="text-destructive text-sm mt-1">{formErrors?.customerPhone}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Indirizzo di consegna *</label>
                      <textarea
                        value={formData?.deliveryAddress}
                        onChange={(e) => setFormData({ ...formData, deliveryAddress: e?.target?.value })}
                        className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                        rows={3}
                        placeholder="Via Roma 123, Macerata"
                      />
                      {formErrors?.deliveryAddress && (
                        <p className="text-destructive text-sm mt-1">{formErrors?.deliveryAddress}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Fascia oraria consegna *</label>
                      <select
                        value={formData?.deliverySlot}
                        onChange={(e) => setFormData({ ...formData, deliverySlot: e?.target?.value })}
                        className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="">Seleziona fascia oraria</option>
                        {deliverySlots?.map((slot) => (
                          <option key={slot?.id} value={slot?.label}>
                            {slot?.label}
                          </option>
                        ))}
                      </select>
                      {formErrors?.deliverySlot && (
                        <p className="text-destructive text-sm mt-1">{formErrors?.deliverySlot}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Note (opzionale)</label>
                      <textarea
                        value={formData?.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                        className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                        rows={2}
                        placeholder="Citofono, piano, ecc."
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCreateOrder}
                  className="w-full bg-primary text-primary-foreground rounded-xl px-6 py-4 hover:bg-primary/90 transition-colors flex items-center justify-center gap-3"
                >
                  <Icon name="CreditCard" size={20} />
                  <span className="font-semibold">Procedi al pagamento</span>
                </button>
              </>
            ) : (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <CheckoutForm
                  clientSecret={clientSecret}
                  orderData={{ orderId, orderTotal: totals?.orderTotal }}
                  onSuccess={handlePaymentSuccess}
                />
              </Elements>
            )}
          </div>

          <div>
            <div className="bg-card border border-border rounded-xl p-6 sticky top-6">
              <h2 className="text-xl font-bold text-foreground mb-4">Riepilogo ordine</h2>
              
              <div className="space-y-3 mb-6">
                {cart?.map((item) => (
                  <div key={item?.id} className="flex justify-between text-sm">
                    <span className="text-foreground">
                      {item?.quantity}x {item?.name}
                    </span>
                    <span className="font-semibold">€{(item?.price * item?.quantity)?.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-2 pt-4 border-t border-border">
                <div className="flex justify-between text-foreground">
                  <span>Totale prodotti</span>
                  <span className="font-semibold">€{totals?.productsTotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-foreground">
                  <span>Costo consegna</span>
                  <span className="font-semibold">€{totals?.deliveryFee?.toFixed(2)}</span>
                </div>
                <div className="pt-2 border-t border-border flex justify-between text-lg">
                  <span className="font-bold text-foreground">Totale</span>
                  <span className="font-bold text-primary">€{totals?.orderTotal?.toFixed(2)}</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-border">
                <div className="flex items-start gap-3 text-sm text-muted-foreground">
                  <Icon name="Info" size={16} className="mt-0.5 flex-shrink-0" />
                  <p>Pagamento online obbligatorio. Il costo di consegna è sempre separato dal totale prodotti.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodCheckout;